// Phase 1: Calculate accumulation phase with Dollar-Cost Averaging (DCA)
// This phase shows portfolio growth toward retirement target including monthly contributions
export const calculateAccumulationPhase = (startingBTC, startPrice, assumptions, targetValue, yearsUntilRetirement, monthlyContribution) => {
  const totalMonths = yearsUntilRetirement * 12;
  const monthlyGrowthRate = Math.pow(1 + assumptions.bitcoinGrowthRate / 100, 1 / 12) - 1;
  
  // Initialize tracking variables
  let currentBTC = startingBTC; // Start with initial Bitcoin holdings
  let currentPrice = startPrice; // Track price month by month
  const data = [];
  
  // Monthly DCA simulation loop
  for (let month = 0; month <= totalMonths; month++) {
    // Step 1: Apply one month of price growth (except for month 0)
    if (month > 0) {
      currentPrice = currentPrice * (1 + monthlyGrowthRate);
    }
    
    // Step 2: Add monthly contribution by purchasing Bitcoin at current price
    if (month > 0 && monthlyContribution > 0) {
      const btcPurchased = monthlyContribution / currentPrice;
      currentBTC += btcPurchased;
    }
    
    // Calculate current portfolio value
    const portfolioValue = currentBTC * currentPrice;
    
    // Store data points for charting (every 12 months for yearly display)
    if (month % 12 === 0) {
      const year = month / 12;
      data.push({
        year,
        price: currentPrice,
        portfolioValue,
        targetValue, // Reference line showing retirement goal
        btcAmount: currentBTC // Total BTC accumulated including DCA
      });
    }
  }
  
  return {
    data,
    finalBTC: currentBTC,
    finalPrice: currentPrice,
    finalValue: currentBTC * currentPrice
  };
};

// Phase 2: Calculate retirement phase (customizable duration of retirement with strategy-specific logic)
// This phase simulates the chosen retirement strategy over the user-defined retirement duration
export const calculateRetirementPhase = (startingBTC, startPrice, assumptions, retirementDuration, retirementStrategy, spendingPlan) => {
  console.log(`Calculating retirement phase with strategy: ${retirementStrategy}`);
  // MODIFIED: Using customizable retirementDuration instead of hardcoded 30 years
  const retirementYears = retirementDuration;
  const data = [];
  let currentBTC = startingBTC;
  let currentLoanPrincipal = 0;
  let viable = true;
  let yearsUntilDepletion = null;
  
  for (let year = 0; year <= retirementYears; year++) {
    // Calculate Bitcoin price with post-retirement growth rate (typically lower)
    const price = startPrice * Math.pow(1 + assumptions.postRetirementBitcoinGrowth / 100, year);
    const portfolioValue = currentBTC * price;
    
    // Determine base income based on retirement phase
    let baseIncome;
    if (year <= 10) {
      baseIncome = spendingPlan.active;
    } else if (year <= 20) {
      baseIncome = spendingPlan.slow;
    } else {
      baseIncome = spendingPlan.later;
    }
    
    // Apply inflation adjustment to base income
    let currentIncome = baseIncome;
    if (year > 0) {
      // Apply compound inflation from year 1 of retirement
      currentIncome = baseIncome * Math.pow(1 + assumptions.inflationRate / 100, year);
    }
    
    if (retirementStrategy === 'sell') {
      // SELL FOR INCOME STRATEGY: Gradually sell Bitcoin to fund living expenses
      // Portfolio balance and BTC holdings decrease over time
      if (year > 0 && currentBTC > 0) {
        const btcToSell = currentIncome / price;
        currentBTC = Math.max(0, currentBTC - btcToSell);
        
        if (year <= 3) {
          console.log(`Sell strategy Year ${year}: Selling ${btcToSell.toFixed(4)} BTC, remaining: ${currentBTC.toFixed(4)} BTC`);
        }
        
        // Check if we've run out of Bitcoin
        if (currentBTC === 0 && !yearsUntilDepletion) {
          yearsUntilDepletion = year;
          viable = false;
        }
      }
      
      data.push({
        year,
        price,
        btcHoldings: currentBTC,
        portfolioValue: currentBTC * price,
        annualIncome: currentIncome,
        strategy: 'sell'
      });
      
    } else {
      // BORROW FOR INCOME STRATEGY: Use Bitcoin as collateral, execute loan rollover
      // BTC holdings stay constant, but debt accumulates with interest
      if (year > 0) {
        // Loan Rollover Logic:
        // 1. Calculate interest on previous year's loan principal
        const previousLoanInterest = currentLoanPrincipal * (assumptions.loanInterestRate / 100);
        
        // 2. Calculate new loan amount = old principal + interest + living expenses
        const newLoanAmount = currentLoanPrincipal + previousLoanInterest + currentIncome;
        
        // 3. Calculate LTV ratio = total debt / BTC collateral value
        const ltvRatio = portfolioValue > 0 ? (newLoanAmount / portfolioValue) * 100 : 100;
        
        if (year <= 3) {
          console.log(`Borrow strategy Year ${year}: Loan principal: $${newLoanAmount.toFixed(0)}, LTV: ${ltvRatio.toFixed(1)}%`);
        }
        
        // 4. Check if LTV exceeds safe threshold (75% = liquidation risk)
        if (ltvRatio > 75 && !yearsUntilDepletion) {
          yearsUntilDepletion = year;
          viable = false;
        }
        
        currentLoanPrincipal = newLoanAmount;
      }
      
      data.push({
        year,
        price,
        btcHoldings: currentBTC, // BTC holdings preserved
        portfolioValue,
        loanPrincipal: currentLoanPrincipal,
        ltvRatio: portfolioValue > 0 ? (currentLoanPrincipal / portfolioValue) * 100 : 0,
        annualIncome: currentIncome,
        strategy: 'borrow'
      });
    }
  }
  
  return {
    data,
    viable,
    yearsUntilDepletion,
    finalValue: retirementStrategy === 'sell' 
      ? currentBTC * data[retirementYears].price 
      : data[retirementYears].portfolioValue - currentLoanPrincipal
  };
};